// Shared validation for the `short_text` runtime question card.
//
// Single source of truth so the card (inline error UI + which state it
// emits) and the engine (QuizRuntime._isAnswered, used for progress fill
// and required-question gating) can never drift. Validation is declared
// by the question's input config and enforced here in JS; the server
// re-checks on submit and never trusts the client (see
// docs/SPEC_short_text_and_number_question_cards.md § Validation).
//
// Input config lives on the question content as `content.input`:
//   { required, max_length, placeholder (localized), pattern (regex src) }
// Any field may be absent; absence means "no constraint".

/**
 * Read and normalize the input config off a resolved engine item.
 * Returns stable defaults so callers never have to null-check.
 */
export function shortTextConfig(item) {
  const input = (item && item.item && item.item.content && item.item.content.input) || {};
  const max = Number(input.max_length);
  return {
    required: input.required === true,
    maxLength: Number.isFinite(max) && max > 0 ? Math.floor(max) : null,
    pattern: typeof input.pattern === 'string' && input.pattern.trim() ? input.pattern : null,
    // The localized placeholder field is passed through untouched; the
    // card resolves it against the active locale.
    placeholder: (input.placeholder && typeof input.placeholder === 'object') ? input.placeholder : null,
  };
}

/**
 * Compile a pattern source to a RegExp, or null if it's absent or
 * malformed. A bad author-supplied regex must never throw at runtime;
 * we treat it as "no pattern" and warn once per call site.
 */
function compilePattern(src) {
  if (!src) return null;
  try {
    return new RegExp(src);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[short_text] ignoring invalid pattern: ${src}`);
    return null;
  }
}

/**
 * Evaluate a raw string value against a normalized config.
 *
 * Returns:
 *   - error:    'required' | 'too_long' | 'pattern' | null  (first failing
 *               rule, in display precedence; null when the field is valid)
 *   - valid:    the field may be left as-is (required satisfied, within
 *               length, and — if non-empty — matches the pattern). An
 *               empty optional field is valid.
 *   - answered: there is a non-empty, fully-valid answer. This is the
 *               engine's "answered" notion: drives progress fill and, for
 *               required questions, the advance gate. An empty optional
 *               field is valid but NOT answered.
 */
export function evaluateShortText(rawValue, cfg) {
  const value = typeof rawValue === 'string' ? rawValue : '';
  const trimmedEmpty = value.trim().length === 0;
  const tooLong = cfg.maxLength != null && value.length > cfg.maxLength;
  const re = compilePattern(cfg.pattern);
  const patternMismatch = !trimmedEmpty && re != null && !re.test(value);

  let error = null;
  if (cfg.required && trimmedEmpty) error = 'required';
  else if (tooLong) error = 'too_long';
  else if (patternMismatch) error = 'pattern';

  const valid = error === null;
  const answered = !trimmedEmpty && !tooLong && !patternMismatch;

  return { error, valid, answered };
}

/**
 * Engine convenience: is this item's short_text answer present and valid?
 * Mirrors the per-type checks in QuizRuntime._isAnswered for the other
 * question types. `state` is whatever the card emitted ({ value } | null).
 */
export function isShortTextAnswered(item, state) {
  if (!state || typeof state.value !== 'string') return false;
  return evaluateShortText(state.value, shortTextConfig(item)).answered;
}
